import { useState, useEffect, useMemo } from 'react';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getCRMAssignmentSettings,
  updateCRMAssignmentSettings,
  getCRMAssignmentQueue,
} from '../api';
import { getCompanyEmployees } from '../../api';
import { PillTabs, TableChip } from '../../components/PremiumDesign';
import { V2Table } from '@/common/components';
import { Button, CustomSelect, Switch } from '@/components';
import { useDelayedSkeleton } from '@/common/hooks';

const WEIGHT_CATEGORIES = [
  { key: 'NEW', label: 'New Leads', color: 'bg-blue-400' },
  { key: 'ACTIVE', label: 'In-Progress Deals', color: 'bg-orange-400' },
  { key: 'WAITING', label: 'Waiting / On Hold', color: 'bg-amber-400' },
  { key: 'COMPLETED', label: 'Won', color: 'bg-primary' },
  { key: 'CLOSED', label: 'Lost', color: 'bg-red-400' },
];

export function SmartAssignmentTab({
  companyId,
  isReadOnly = false,
  tabs,
  activeTab,
  onTabChange,
}) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    is_enabled: false,
    strategy: 'balanced',
    open_tasks_weight: 0,
    overdue_followups_weight: 0,
    assignment_delay_seconds: 0,
    max_retries: 0,
    retry_backoff_seconds: 0,
    category_weights: {},
  });

  const {
    data: settings,
    isLoading: isInitialSettingsLoading,
    isFetching: isSettingsFetching,
    isError: isSettingsError,
  } = useQuery({
    queryKey: ['crm-assignment-settings', companyId],
    queryFn: () => getCRMAssignmentSettings(companyId),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSettingsSkeleton = useDelayedSkeleton(
    isSettingsFetching && settings !== undefined,
    300,
    500,
  );
  const isSettingsLoading =
    isInitialSettingsLoading || showDelayedSettingsSkeleton;

  const {
    data: queueData,
    isLoading: isInitialQueueLoading,
    isFetching: isQueueFetching,
    isError: isQueueError,
  } = useQuery({
    queryKey: ['crm-assignment-queue', companyId],
    queryFn: () => getCRMAssignmentQueue(companyId),
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedQueueSkeleton = useDelayedSkeleton(
    isQueueFetching && queueData !== undefined,
    300,
    500,
  );
  const isQueueLoading = isInitialQueueLoading || showDelayedQueueSkeleton;
  const [activityPage, setActivityPage] = useState(1);
  const recent = queueData?.recent || [];
  const activityPageSize = 10;
  const recentPage = recent.slice(
    (activityPage - 1) * activityPageSize,
    activityPage * activityPageSize,
  );
  const activityColumns = useMemo(
    () => [
      {
        id: 'contact',
        label: 'Contact',
        defaultWidth: 220,
        minWidth: 140,
        cell: (item) => (
          <span className="truncate block" title={item.contact_name || ''}>
            {item.contact_name || `ID: ${item.contact_id}`}
          </span>
        ),
      },
      {
        id: 'status',
        label: 'Status',
        defaultWidth: 140,
        minWidth: 110,
        cell: (item) => {
          const status = (item.status || '').toLowerCase();
          const tone =
            status === 'assigned'
              ? 'success'
              : status === 'pending' || status === 'processing'
                ? 'warning'
                : status === 'failed'
                  ? 'error'
                  : 'neutral';
          return <TableChip tone={tone}>{item.status || 'unknown'}</TableChip>;
        },
      },
      {
        id: 'priority',
        label: 'Priority',
        defaultWidth: 110,
        minWidth: 80,
        cell: (item) => item.priority,
      },
      {
        id: 'attempts',
        label: 'Attempts',
        defaultWidth: 110,
        minWidth: 80,
        cell: (item) => item.attempts,
      },
      {
        id: 'date',
        label: 'Date',
        defaultWidth: 170,
        minWidth: 130,
        cell: (item) =>
          item.enqueued_at
            ? new Date(item.enqueued_at).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })
            : 'N/A',
      },
    ],
    [],
  );

  const { data: employeesData } = useQuery({
    queryKey: ['companyEmployees', companyId],
    queryFn: () => getCompanyEmployees(companyId),
    enabled: !!companyId,
  });

  const rawEmployees = employeesData?.results || [];

  const previewEmployees = rawEmployees.slice(0, 3).map((emp, i) => {
    const category_counts = {
      NEW: [0, 2, 1][i % 3],
      ACTIVE: [8, 2, 5][i % 3],
      WAITING: [1, 0, 2][i % 3],
      COMPLETED: [12, 5, 8][i % 3],
      CLOSED: [4, 2, 1][i % 3],
    };
    const open_tasks = [3, 1, 0][i % 3];
    const lastAssignedRank = [2, 1, 3][i % 3];

    let score = 0;
    const cw = formData.category_weights || {};
    score += category_counts.NEW * (cw.NEW ?? 0);
    score += category_counts.ACTIVE * (cw.ACTIVE ?? 0);
    score += category_counts.WAITING * (cw.WAITING ?? 0);
    score += category_counts.COMPLETED * (cw.COMPLETED ?? 0);
    score += category_counts.CLOSED * (cw.CLOSED ?? 0);
    score += open_tasks * (formData.open_tasks_weight ?? 0);

    return {
      ...emp,
      category_counts,
      open_tasks,
      lastAssignedRank,
      score,
    };
  });

  const sortedPreview = [...previewEmployees].sort((a, b) => {
    if (a.score !== b.score) return a.score - b.score;
    return a.lastAssignedRank - b.lastAssignedRank;
  });

  const winner = sortedPreview[0];
  const isEnabled = formData.is_enabled;

  const hasChanges = useMemo(() => {
    if (!settings) return false;
    const originalData = {
      is_enabled: settings.is_enabled ?? false,
      strategy: settings.strategy || 'balanced',
      open_tasks_weight: settings.open_tasks_weight ?? 0,
      overdue_followups_weight: settings.overdue_followups_weight ?? 0,
      assignment_delay_seconds: settings.assignment_delay_seconds ?? 0,
      max_retries: settings.max_retries ?? 0,
      retry_backoff_seconds: settings.retry_backoff_seconds ?? 0,
      category_weights: settings.category_weights || {},
    };
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, settings]);

  useEffect(() => {
    if (settings) {
      setFormData({
        is_enabled: settings.is_enabled ?? false,
        strategy: settings.strategy || 'balanced',
        open_tasks_weight: settings.open_tasks_weight ?? 0,
        overdue_followups_weight: settings.overdue_followups_weight ?? 0,
        assignment_delay_seconds: settings.assignment_delay_seconds ?? 0,
        max_retries: settings.max_retries ?? 0,
        retry_backoff_seconds: settings.retry_backoff_seconds ?? 0,
        category_weights: settings.category_weights || {},
      });
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: (payload) => {
      if (isReadOnly) throw new Error('Permission denied.');
      return updateCRMAssignmentSettings({ companyId, payload });
    },
    onSuccess: () => {
      toast.success('Assignment settings updated successfully!');
      queryClient.invalidateQueries(['crm-assignment-settings', companyId]);
    },
    onError: () => {
      toast.error('Failed to update assignment settings.');
    },
  });

  const handleChange = (field, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCategoryWeightChange = (category, value) => {
    if (isReadOnly) return;
    setFormData((prev) => ({
      ...prev,
      category_weights: {
        ...(prev.category_weights || {}),
        [category]: value,
      },
    }));
  };

  const handleSave = () => {
    const payload = {
      is_enabled: formData.is_enabled,
      strategy: formData.strategy,
      open_tasks_weight: Number(formData.open_tasks_weight) || 0,
      overdue_followups_weight: Number(formData.overdue_followups_weight) || 0,
      assignment_delay_seconds: Number(formData.assignment_delay_seconds) || 0,
      max_retries: Number(formData.max_retries) || 0,
      retry_backoff_seconds: Number(formData.retry_backoff_seconds) || 0,
      category_weights: Object.fromEntries(
        Object.entries(formData.category_weights || {}).map(([k, v]) => [
          k,
          v === '' ? 0 : Number(v) || 0,
        ]),
      ),
    };
    updateMutation.mutate(payload);
  };

  if (isSettingsLoading) {
    return (
      <div className="flex flex-col h-full pt-4 animate-pulse">
        <div className="w-full flex flex-col gap-6 pb-6 pr-2">
          <div className="flex items-center justify-between gap-3">
            <div className="h-6 w-40 bg-form-background-light rounded"></div>
            <div className="h-9 w-64 bg-form-background-light rounded-full"></div>
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col gap-2">
              <div className="h-5 w-40 bg-form-background-light rounded"></div>
              <div className="h-4 w-80 bg-form-background-light rounded"></div>
            </div>
            <div className="h-10 w-32 bg-form-background-light rounded-xl"></div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
            <div className="flex flex-col gap-6">
              <div className="bg-form-background-light rounded-2xl p-5 flex flex-col gap-5">
                <div className="h-5 w-40 bg-white rounded"></div>
                <div className="h-16 w-full bg-white rounded-xl"></div>
                <div className="h-11 w-full bg-white rounded-xl"></div>
              </div>
              <div className="bg-form-background-light rounded-2xl p-5 flex flex-col gap-4">
                <div className="h-5 w-40 bg-white rounded"></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="flex flex-col gap-3">
                      <div className="h-4 w-24 bg-white rounded"></div>
                      <div className="h-11 w-full bg-white rounded-xl"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-form-background-light rounded-2xl p-5 flex flex-col gap-4">
              <div className="h-5 w-32 bg-white rounded"></div>
              <div className="h-8 w-52 bg-white rounded-full"></div>
              <div className="h-16 w-full bg-white rounded-xl"></div>
              <div className="flex flex-col gap-2">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="h-16 w-full bg-white rounded-xl"
                  ></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isSettingsError) {
    return (
      <div className="flex flex-col h-full pt-4">
        <div className="w-full rounded-2xl bg-form-background-light p-6 text-center text-status-error">
          Failed to load Smart Assignment settings.
        </div>
      </div>
    );
  }

  const strategyOptions = [
    {
      value: 'balanced',
      label: 'Balanced (recommended) - lightest workload first',
    },
    { value: 'round_robin', label: 'Round Robin - coming soon / not used yet' },
    { value: 'weighted', label: 'Weighted - coming soon / not used yet' },
  ];

  const actualStrategyOptions = strategyOptions.find(
    (o) => o.value === formData.strategy,
  )
    ? strategyOptions
    : [
        ...strategyOptions,
        { value: formData.strategy, label: formData.strategy },
      ];

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {tabs && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0 mb-4">
          <h4 className="text-[22px] leading-[28px] font-bold font-sf-pro-display text-[#1F2241]">
            Auto Assignment
          </h4>
          <PillTabs items={tabs} value={activeTab} onChange={onTabChange} />
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar flex flex-col gap-6 pb-8 pr-2">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div>
            <h3 className="text-[20px] font-medium text-primary mb-1">
              Auto-Assign Leads
            </h3>
            <p className="text-sm text-text-muted max-w-3xl">
              When a new contact arrives with no owner, the system can pick a
              fair teammate automatically. Adjust the settings below and watch
              the live preview to see who would get the next lead.
            </p>
          </div>
          {!isReadOnly && (
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending}
              className="h-[40px] px-6 font-bold shrink-0"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-stretch">
          {/* Left Column: Engine settings */}
          <div className="flex flex-col gap-6">
            <section className="bg-form-background-light rounded-2xl p-4 flex flex-col gap-5">
              <div>
                <h3 className="text-[18px] font-medium text-primary mb-1">
                  Engine Confirmation
                </h3>
                <p className="text-sm text-text-muted">
                  Fine-tune how the auto-assignment engine distributes leads
                  across your team.
                </p>
              </div>

              <div className="flex items-start gap-3 p-3.5 rounded-xl bg-white shadow-sm">
                <Switch
                  checked={formData.is_enabled}
                  onChange={(val) => handleChange('is_enabled', val)}
                  disabled={updateMutation.isPending || isReadOnly}
                  className="mt-0.5"
                  aria-label="Enable Auto-Assignment Engine"
                />
                <div>
                  <p className="text-sm font-bold text-text-primary">
                    Enable Auto-Assignment Engine
                  </p>
                  <p className="text-sm text-text-muted mt-1">
                    When enabled, unassigned incoming contacts will
                    automatically be queued and routed to the most appropriate
                    team member.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-text-secondary">
                  Routing Strategy
                </label>
                <CustomSelect
                  options={actualStrategyOptions}
                  value={formData.strategy}
                  onChange={(val) => handleChange('strategy', val)}
                  disabled={updateMutation.isPending || isReadOnly}
                  placeholder="Select routing strategy..."
                  className="w-full h-11"
                  buttonClassName="w-full h-full flex items-center justify-between rounded-xl bg-white border border-border-default text-text-primary text-sm focus:outline-none focus:border-primary px-4"
                />
                <p className="text-sm text-text-muted">
                  Balanced strategy prioritizes team members with the lowest
                  workload score.
                </p>
              </div>
            </section>

            <section className="bg-form-background-light rounded-2xl p-4 flex flex-col gap-4">
              <div>
                <h3 className="text-[18px] font-medium text-primary mb-1">
                  Workload Weights
                </h3>
                <p className="text-sm text-text-muted">
                  Assign a weight to different pipeline stages. Higher weights
                  signify a heavier workload, making a team member less likely
                  to receive the next lead.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {WEIGHT_CATEGORIES.map((cat) => (
                  <div key={cat.key} className="flex flex-col gap-3">
                    <label className="text-sm font-semibold text-text-secondary flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${cat.color}`} />
                      {cat.label}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.category_weights?.[cat.key] ?? 0}
                      onChange={(e) =>
                        handleCategoryWeightChange(cat.key, e.target.value)
                      }
                      disabled={updateMutation.isPending || isReadOnly}
                      className="w-full h-11 px-4 rounded-xl bg-white border border-border-default text-text-primary text-sm focus:outline-none focus:border-primary transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                    />
                  </div>
                ))}
              </div>
            </section>
          </div>

          {/* Right Column: Live Preview */}
          <section className="bg-form-background-light rounded-2xl p-4 flex flex-col gap-4 h-full min-h-0 overflow-hidden">
            <div className="shrink-0">
              <h3 className="text-[18px] font-medium text-primary mb-1">
                Live Preview
              </h3>
              <p className="text-sm text-text-muted">
                See how your current settings rank the team in real-time.
              </p>
            </div>

            <div
              className={`shrink-0 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold w-fit ${
                isEnabled
                  ? 'bg-surface-mint text-primary'
                  : 'bg-white text-text-muted border border-border-default'
              }`}
            >
              <span
                className={`w-1.5 h-1.5 rounded-full ${isEnabled ? 'bg-primary' : 'bg-text-muted'}`}
              />
              {isEnabled
                ? `Active - ${formData.strategy === 'balanced' ? 'Balanced' : 'Round Robin'} Strategy`
                : 'Auto-assignment Paused'}
            </div>

            {isEnabled && winner && (
              <div className="shrink-0">
                <p className="text-md font-medium text-[#1F2241] mb-2">
                  Next In Line
                </p>
                <div className="bg-surface-mint border border-primary rounded-xl p-4 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg shrink-0">
                    {(winner.name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-base font-bold text-text-primary">
                      {winner.name || 'Unknown'}
                    </p>
                    <p className="text-sm font-medium text-primary">
                      Least busy right now
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2 flex-1 min-h-0">
              <p className="text-md font-medium text-[#1F2241] shrink-0">
                Team Ranking
              </p>
              <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1">
                {sortedPreview.map((emp, idx) => {
                  const isWinner = isEnabled && idx === 0;
                  return (
                    <div
                      key={emp.id || idx}
                      className="flex items-center justify-between gap-4 p-3 rounded-xl border border-border-default bg-white"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div
                          className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-bold text-sm ${
                            isWinner
                              ? 'bg-primary text-white'
                              : 'bg-form-background-light text-text-muted'
                          }`}
                        >
                          {(emp.name || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-text-primary text-sm flex items-center gap-2 truncate">
                            {emp.name || 'Unknown'}
                            {isWinner && (
                              <span className="text-[9px] bg-primary text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-wider shrink-0">
                                1st
                              </span>
                            )}
                          </p>
                          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-sm text-text-muted">
                            <span
                              className="flex items-center gap-1 shrink-0"
                              title="New Leads"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
                              New:{' '}
                              <span className="font-semibold text-text-primary">
                                {emp.category_counts.NEW}
                              </span>
                            </span>
                            <span
                              className="flex items-center gap-1 shrink-0"
                              title="In-Progress Deals"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-orange-400"></span>
                              Active:{' '}
                              <span className="font-semibold text-text-primary">
                                {emp.category_counts.ACTIVE}
                              </span>
                            </span>
                            <span
                              className="flex items-center gap-1 shrink-0"
                              title="Waiting"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span>
                              Wait:{' '}
                              <span className="font-semibold text-text-primary">
                                {emp.category_counts.WAITING}
                              </span>
                            </span>
                            <span
                              className="flex items-center gap-1 shrink-0"
                              title="Open Tasks"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-purple-400"></span>
                              Tasks:{' '}
                              <span className="font-semibold text-text-primary">
                                {emp.open_tasks}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-bold text-text-primary leading-none">
                          {emp.score}
                        </p>
                        <p className="text-[10px] text-text-muted uppercase font-medium">
                          Score
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </div>

        {/* Queue Section */}
        <section className="flex flex-col gap-4 w-full mt-2">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-[20px] font-medium text-primary">
                Auto-Assign Leads
              </h3>
              <p className="text-sm text-text-muted">
                Monitor the background distribution process in real-time.
              </p>
            </div>
            {queueData?.counts && (
              <div className="flex items-center gap-2 shrink-0">
                {Object.entries(queueData.counts).map(([status, count]) => {
                  const isAssigned = status.toLowerCase() === 'assigned';
                  const isPending =
                    status.toLowerCase() === 'pending' ||
                    status.toLowerCase() === 'processing';
                  const tone = isAssigned
                    ? 'success'
                    : isPending
                      ? 'warning'
                      : 'neutral';
                  return (
                    <TableChip
                      key={status}
                      tone={tone}
                      className="capitalize gap-1.5"
                    >
                      {status}{' '}
                      <span className="font-bold">
                        {String(count).padStart(2, '0')}
                      </span>
                    </TableChip>
                  );
                })}
              </div>
            )}
          </div>

          {isQueueError ? (
            <div className="text-status-error text-sm p-4 bg-status-error/10 rounded-xl border border-status-error/20">
              Failed to load Assignment Queue.
            </div>
          ) : (
            <div className="w-full min-w-0 max-h-[420px] flex flex-col">
              <p className="text-xs text-text-muted px-1 pb-2 shrink-0">
                Recent queue activity returned by the server. This list is not a
                full paged history.
              </p>
              <V2Table
                tight
                bare
                storageKey="crm_assignment_activity_column_widths"
                columns={activityColumns}
                data={isQueueLoading ? [] : recentPage}
                rowKey={(item, index) =>
                  item.id || `${item.contact_id}-${item.enqueued_at}-${index}`
                }
                loading={isQueueLoading || !queueData}
                empty="The queue is currently empty."
                pagination={{
                  currentPage: activityPage,
                  totalCount: recent.length,
                  itemsPerPage: activityPageSize,
                  onChange: setActivityPage,
                }}
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
